import React, { useState } from 'react';
import { IContentRecord } from '@/lib/types/resolution-types';
import CopyButton from '../CopyButton';
import ExpandFieldBtn from '../ExpandFieldBtn';
import ExpandableField from './ExpandableField';
import { get } from "lodash-es"
import { resolveQs } from "@/lib/utils/commonUtils";
import Link from "next/link";
import countryCodeLookup from "@/lib/types/country-code-lookup";
import languageCodeLookup from "@/lib/types/language-code-lookup";
import LoadingIcon from "@/components/LoadingIcon";
import { Button } from "@/components/ui/button"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"
export default function BaseObjectMetadata({
    record,
    hasFullRecord = true,
    defaultOpen = true,
    mode = "record",
    onOpen,
    isLoading = false
}: {
    record: IContentRecord;
    hasFullRecord: boolean;
    defaultOpen?: boolean;
    mode?: string
    onOpen?: Function,
    isLoading: boolean
}) {
    const [baseMdOpen, setBaseMdOpen] = useState(hasFullRecord || defaultOpen);
    const [isOpen, setIsOpen] = useState(hasFullRecord);

    const {
        ResourceName,
        ID,
        ReferentType,
        CountryOfOrigin,
        OriginalLanguages,
        ReleaseDate,
        ...rest
    } = record;

    const getCountryName = (countryCode) => {
        if (Array.isArray(countryCode)) {
            return countryCode.map(item => countryCodeLookup[item] || item);
        }
        return countryCodeLookup[countryCode] || countryCode;
    }

    const getLanguageName = (languageCode) => {
        if (Array.isArray(languageCode)) {
            return languageCode.map(item => languageCodeLookup[item]?.name || item);
        }
        return languageCodeLookup[languageCode]?.name || languageCode;
    }

    const datalist = {
        class: {
            value: get(record, 'ResourceName._titleClass', '-'),
        },
        country: {
            value: getCountryName(get(record, 'CountryOfOrigin[0]', '-')),
            length: get(record, 'CountryOfOrigin.length', 0),
            values: getCountryName(get(record, 'CountryOfOrigin', [])),
        },
        language: {
            value: getLanguageName(get(record, 'OriginalLanguage[0].value', '-')),
            length: get(record, 'OriginalLanguage.length', 0),
            values: getLanguageName(get(record, 'OriginalLanguage', [])),
        },
        'release date': {
            value:
                String(get(record, 'ReleaseDate', ''))
                    .split('-')
                    .join('/') || '-',
        },
    };

    const [isShowCountries, setIsShowCountries] = useState(false);
    const [isShowLanguages, setIsShowLanguages] = useState(false);

    const showCountries = () => {
        setIsShowCountries(!isShowCountries);
    };

    const showLanguages = () => {
        setIsShowLanguages(!isShowLanguages);
    };

    let classType = ["-"];

    const ExtraObjectMetadata = record.ExtraObjectMetadata;

    if (ExtraObjectMetadata) {
        const [key, value] = Object.entries(ExtraObjectMetadata)[0];
        //console.log(key, value);
        if (key.indexOf("Info") > -1) {
            classType = value[key.replace("Info", "Class")];
            if (!Array.isArray(classType)) {
                classType = [classType]
            }
        }
    }

    const onOpenHandler = () => {
        if (!baseMdOpen) {
            onOpen && onOpen(baseMdOpen === true ? false : true);
        }
        setBaseMdOpen(baseMdOpen === true ? false : true);
    }

    const handleToggle = () => {
        setIsOpen(!isOpen);
    }

    //{ResourceName?._titleClass ? ResourceName._titleClass : '-'}

    //<dt className="flex-col md:flex-row justify-center md:justify-between w-full text-center">

    return (
        <div className="base-object-metadata">
            <header className="animate__animated animate__fadeIn  ">
                <div
                    className="flex justify-between items-start md:items-center mb-2 ">
                    {mode === "record" ?
                        <h1 className="text-xl md:text-3xl">{ResourceName?.value}</h1>
                        : <h3 className="text-lg md:text-xl">{ResourceName?.value}</h3>}
                    <div className="flex justify-between items-center">
                        <h4 className="text-lg">{ReferentType}</h4>
                        {isLoading && <div className="py-1 px-4"><LoadingIcon></LoadingIcon></div>}
                        {(mode === "card" && !hasFullRecord && !isLoading) && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            className="cursor-pointer flex items-center"
                                            onClick={onOpenHandler}
                                        >
                                            <i className="mio" >arrow_downward</i>
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent variant="tooltip" className="text-sm">
                                        <p>Load Additonal Metadata</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                </div>
                <hr />
                <div className="grid grid-cols-3 gap-2  md:gap-4 items-start mt-2">
                    <div className="col-span-3 lg:col-span-1 flex items-center">
                        {mode === "record" ?
                            <span className="mr-2 text-theme-blue">{ID}</span> :
                            <Link className="mr-2 text-blue-500 underline" href={resolveQs(ID)}>
                                {ID}
                            </Link>
                        }
                        <CopyButton content={ID} />
                    </div>
                    <div className="col-span-3 lg:col-span-2 md:px-4">
                        <dl className="grid grid-cols-4 text-center gap-2 mt-2 w-full items-start">
                            <div className="flex flex-col items-start">
                                <dt>Class</dt>
                                <dd>
                                    {classType.join(" | ")}
                                </dd>
                            </div>
                            <div className="flex flex-col justify-center items-start">
                                <dt className="flex flex-row justify-between w-full">
                                    <span>Country</span>{datalist.country.values.length > 2 && (
                                        <button
                                            className="px-2 text-sm flex justify-center items-center hover:hover:text-blue-500"
                                            type="button"
                                            onClick={showCountries}
                                        >
                                            +{datalist.country.values.length - 1}
                                            <i className="mio">{isShowCountries ? "keyboard_arrow_up" : "keyboard_arrow_down"}</i>
                                        </button>
                                    )}</dt>
                                <dd className="flex flex-col md:justify-content-start justify-start items-start w-full">
                                    <span>{datalist.country.value}</span>
                                    {isShowCountries && datalist.country.values.length > 2 &&
                                        (
                                            datalist.country.values.map((item, index) => {
                                                if (index > 0) {
                                                    return (<span key={"country-" + index}>{item}</span>)
                                                }
                                            }
                                            )
                                        )
                                    }

                                </dd>
                            </div>
                            <div className="flex flex-col justify-center items-start">
                                <dt className="flex flex-row justify-between w-full text-center">
                                    <span>Language</span> {datalist.language.values.length > 2 && (
                                        <button
                                            className="px-2 text-sm flex justify-center items-center hover:text-blue-600"
                                            type="button"
                                            onClick={showLanguages}
                                        >
                                            +{datalist.country.values.length - 1}
                                            <i className="mio">{isShowLanguages ? "keyboard_arrow_up" : "keyboard_arrow_down"}</i>
                                        </button>
                                    )}</dt>
                                <dd className="flex flex-col justify-start items-start w-full">
                                    <span>{datalist.language.value}</span>
                                    {isShowLanguages && datalist.language.values.length > 2 &&
                                        (
                                            datalist.language.values.map((item, index) => {
                                                if (index > 0) {
                                                    return (<span key={"lang-" + index}>{item}</span>)
                                                }
                                            }
                                            )
                                        )
                                    }
                                </dd>
                            </div>
                            <div className="flex flex-col justify-self-end items-start">
                                <dt><span className="hidden md:block">Release Date</span>
                                    <span className=" md:hidden">Date</span></dt>
                                <dd>{ReleaseDate}</dd>
                            </div>

                        </dl>
                    </div>
                </div>
            </header>
            {baseMdOpen && (
                <div>
                    <button
                        className="flex w-full justify-between my-2 cursor-pointer animate__animated animate__fadeIn"
                        onClick={handleToggle}
                        type="button">
                        <h3 className="text-lg">Base Object</h3>
                        <div
                            className="rounded px-2 py-1 text-sm flex flex-row justify-between items-center">
                            <i className="mio">{isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</i>
                        </div>
                    </button>
                    {!isOpen && <hr />}
                    {isOpen && (
                        <div className="my-4">
                            <ExpandableField defaultOpen canClose={false} data={{ data: rest }} />
                        </div>
                    )}

                </div>
            )}

        </div>
    );
}
